import { ZERO_WEI } from '@kwenta/sdk/constants'
import { formatCryptoCurrency, formatCurrency } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { NetworkId } from '@kwenta/sdk/types'
import { TransactionButton } from 'components/Button/Button'
import { notifyError } from 'components/ErrorNotifier'
import ErrorView from 'components/ErrorView'
import { InfoBoxCol } from 'components/InfoBox'
import NumericInput from 'components/Input/NumericInput'
import Loader from 'components/Loader'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { FlexDivCol } from 'components/layout/flex'
import Connector from 'containers/Connector'
import isEqual from 'lodash/isEqual'
import { selectBalancesAllProviders } from 'state/balances/selectors'
import {
	depositAccountKeeperBalance,
	withdrawAccountKeeperBalance,
} from 'state/futures/isolatedMargin/actions'
import {
	selectKeeperEthBalance,
	selectSnxV2IsolatedBalanceInfo,
} from 'state/futures/isolatedMargin/selectors'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode, selectWallet } from 'state/wallet/selectors'
import { BalanceContainer, BalanceText } from 'styles/common'
import logError from 'utils/logError'

type TransferType = 'deposit' | 'withdraw'

type Props = {
	defaultType?: TransferType
}

const DEPOSIT_ENABLED = true

export default function ManageKeeperBalanceSmartMargin({ defaultType = 'deposit' }: Props) {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { client, network } = Connector.useContainer()
	const walletAddress = useAppSelector(selectWallet)
	const walletBalances = useAppSelector(selectBalancesAllProviders, isEqual)
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const { keeperEthBal } = useAppSelector(selectSnxV2IsolatedBalanceInfo)
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)

	const [amount, setAmount] = useState('')
	const [isMax, setMax] = useState(false)
	const [userEthBal, setUserEthBal] = useState(ZERO_WEI)
	const [transferType, setTransferType] = useState(defaultType === 'deposit' ? 0 : 1)

	const keeperBalance = useAppSelector(selectKeeperEthBalance)

	const withdrawableBalance = useMemo(() => wei(keeperBalance), [keeperBalance])

	useEffect(() => {
		const getUserEthBal = async () => {
			if (!walletAddress || !client) return
			try {
				const bal = walletBalances?.[network.id as NetworkId]?.ETH?.balance ?? ZERO_WEI
				setUserEthBal(wei(bal))
			} catch (err) {
				notifyError('Failed to read ETH balance', err)
				logError(err)
			}
		}

		getUserEthBal()
	}, [walletAddress, client, network.id, walletBalances])

	const onWithdrawKeeperDeposit = useCallback(() => {
		if (wei(keeperEthBal).eq(0)) return
		dispatch(withdrawAccountKeeperBalance(wei(amount)))
		setAmount('')
	}, [dispatch, amount, keeperEthBal])

	const onDepositKeeperDeposit = useCallback(async () => {
		if (!amount) return
		dispatch(depositAccountKeeperBalance(wei(amount)))
		setAmount('')
	}, [amount, dispatch])

	const exceedsLimit = useMemo(() => {
		const amtWei = wei(amount || 0)
		return amtWei.gt(transferType === 0 ? userEthBal : withdrawableBalance)
	}, [transferType, amount, userEthBal, withdrawableBalance])

	const isDisabled = useMemo(() => {
		if (isSubAccountMode || !amount || isSubmitting || exceedsLimit || Number(amount) === 0)
			return true
		return false
	}, [isSubAccountMode, amount, isSubmitting, exceedsLimit])

	const handleSetMax = useCallback(() => {
		setMax(true)
		setAmount(transferType === 0 ? userEthBal.toString() : withdrawableBalance.toString())
	}, [withdrawableBalance, userEthBal, transferType])

	const onChangeTab = (selection: number) => {
		setTransferType(selection)
		setAmount('')
	}

	return (
		<FlexDivCol>
			<InfoBoxContainer>
				<InfoBoxCol
					title={t('futures.modals.manage.order.total-balance')}
					textValue={formatCryptoCurrency(keeperBalance, {
						maxDecimals: 6,
						removeTrailingZeros: true,
						currencyKey: 'ETH',
					})}
				/>
				<InfoBoxCol
					title={t('futures.modals.manage.order.withdrawable')}
					textValue={formatCryptoCurrency(withdrawableBalance, {
						maxDecimals: 6,
						removeTrailingZeros: true,
						currencyKey: 'ETH',
					})}
				/>
			</InfoBoxContainer>
			{DEPOSIT_ENABLED && (
				<StyledSegmentedControl
					values={['Deposit', 'Withdraw']}
					selectedIndex={transferType}
					onChange={onChangeTab}
				/>
			)}

			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
				<BalanceText onClick={handleSetMax}>
					<span>
						{formatCurrency('ETH', transferType === 0 ? userEthBal : withdrawableBalance, {
							currencyKey: 'ETH',
						})}
					</span>
				</BalanceText>
			</BalanceContainer>

			<NumericInput
				dataTestId="futures-market-trade-withdraw-margin-input"
				placeholder={'0.0'}
				value={amount}
				invalid={exceedsLimit}
				onChange={(_, v) => {
					if (isMax) setMax(false)
					setAmount(v)
				}}
			/>
			<Spacer height={20} />

			<TransactionButton
				data-testid="futures-market-trade-withdraw-margin-button"
				disabled={isDisabled}
				fullWidth
				onClick={transferType === 0 ? onDepositKeeperDeposit : onWithdrawKeeperDeposit}
			>
				{isSubmitting ? (
					<Loader />
				) : (
					t(
						`futures.market.trade.orders.manage-keeper-deposit.${
							transferType === 0 ? 'deposit' : 'withdraw'
						}`
					)
				)}
			</TransactionButton>

			<Spacer height={20} />
			{transferType === 1 && withdrawableBalance.lt(keeperBalance) && (
				<ErrorView
					messageType="warn"
					message={t('futures.modals.manage.order.cancel-all-warning')}
					containerStyle={{ marginBottom: '0px' }}
				/>
			)}
			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-keeper-bal-management')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
}

const StyledSegmentedControl = styled(SegmentedControl)`
	margin: 16px 0;
`

const InfoBoxContainer = styled.div`
	box-sizing: border-box;
	width: 100%;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 8px;
	padding: 15px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	grid-gap: 15px 15px;
`
